import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SalesService from "../../../../services/sales_service";
import EnrollmentService from "../../../../services/enrollment";
import { saleDefaults } from '../../../../models/sale';
import { parseDateInput } from '../../../../utils/date';
import { useMembers } from "../../../../hooks/useMembers";

export const useSalesProcess = ({ 
    idTenant, 
    user, 
    id, 
    idMember, 
    memberName, 
    memberPhone,
    memberEmail,
    memberPhotoUrl,
    idConsultant, 
    consultantName,
    consultantEmail,
    consultantPhone,
    consultantPhotoUrl
}) => {
    const navigate = useNavigate();
    const { addOrUpdateMember } = useMembers();
    const [isProcessing, setIsProcessing] = useState(false);
    const [saleSuccessData, setSaleSuccessData] = useState(null);
    const [showReceiptModal, setShowReceiptModal] = useState(false);

    const handleCloseReceipt = useCallback(() => {
        setShowReceiptModal(false);
        navigate(`/members/${id}`);
    }, [navigate, id]);

    const handleFinalizeSale = useCallback(async ({ finalizeData, saleDate, startDate, cartItems, payments, totals, pendingEnrollments, idCashierShift }) => {
        if (!id) {
            toast.error("ID do membro não encontrado.");
            return;
        }

        try {
            setIsProcessing(true);


            // Evitar bugs de fuso horário padronizando input dates
            const normalizedSaleDate = parseDateInput(saleDate);
            const normalizedStartDate = parseDateInput(startDate || saleDate);

            const payload = {
                ...saleDefaults,
                idMember: id,                    // Firestore document ID do membro
                memberNumber: idMember,          // ID sequencial (exibição)
                memberName,
                memberPhone,
                memberEmail,
                memberPhotoUrl,
                
                idConsultant: idConsultant || null,
                consultantName: consultantName || null,
                consultantEmail: consultantEmail || null,
                consultantPhone: consultantPhone || null,
                consultantPhotoUrl: consultantPhotoUrl || null,
                
                idEmployee: user?.uid || null,
                employeeName: user?.displayName || user?.fullName || user?.name || user?.email || 'Sistema',
                employeeEmail: user?.email || '',
                employeePhone: user?.phone || '',
                employeePhotoUrl: user?.photoURL || user?.photoUrl || '',
                tenantName: user?.tenantName || 'NeoApp',
                idCashierShift: idCashierShift || null,
                date: normalizedSaleDate,
                startDate: normalizedStartDate,
                items: cartItems.map(item => ({
                    ...item,
                    type: item.type,
                    idItem: item.idItem,
                    name: item.name,
                    quantity: 1,
                    unitPrice: item.unitPrice,
                    totalPrice: item.totalPrice,
                    isScholarship: item.isScholarship || false,
                    startDate: normalizedStartDate
                })),
                paymentMethods: payments.map(p => ({
                    methodId: p.methodId, // já é string ('CASH', 'PIX', etc.)
                    methodLabel: p.methodLabel,
                    value: p.value,
                    installments: parseInt(p.installments) || 1,
                    idAcquirer: p.provider || null,
                    provider: p.providerName || null,
                    brand: p.brand || null,
                    auth: p.auth || null,
                    netValue: p.netValue
                })),
                subtotal: totals.subtotal,
                discountTotal: totals.discount,
                totalAmount: totals.total,
                totalPaid: totals.totalPaid,
                balance: totals.balance,
                surplus: totals.surplus,
                dueDateBalance: finalizeData.dueDate || null,
                status: totals.balance > 0.01 ? 'partial' : 'paid'
            };

            const result = await SalesService.processSale(idTenant, user, payload);

            // --- INÍCIO DA MATRÍCULA EM LOTE (BACKGROUND) ---
            const memberShipItems = cartItems.filter(i => !!i.enrollmentRequired && pendingEnrollments[i.cartId] && pendingEnrollments[i.cartId].length > 0);
            
            if (memberShipItems.length > 0) {
               for (const item of memberShipItems) {
                   try {
                       const specificMemberMembershipId = result.createdMemberships?.[item.cartId];
                       
                       await EnrollmentService.enrollMember(idTenant, user, {
                           idMember: id,  // Passa o doc.id para o enrollment
                           idMembership: specificMemberMembershipId || item.idItem, // usa a membership comprada ou recai
                           selectedClasses: pendingEnrollments[item.cartId],
                           memberName: memberName,
                           skipStatusCheck: true
                       });
                   } catch (err) {
                       console.error('Falha ao matricular item do carrinho:', item.name, err);
                       toast.warning(`A venda foi concluída mas falhou a matrícula automática da turma para: ${item.name}`);
                   }
               }
            }
            // --- FIM DA MATRÍCULA ---

            setSaleSuccessData({ ...payload, saleNumber: result.saleNumber });
            setShowReceiptModal(true);
            toast.success("Venda e matrícula(s) realizadas com sucesso!");

            // Atualiza o cache local de membros, já que a venda ativa a conta
            addOrUpdateMember({ id, status: "active" });

        } catch (error) {
            console.error("Erro ao finalizar venda:", error);
            toast.error(error.message || "Erro ao processar venda");
        } finally {
            setIsProcessing(false);
        }
    }, [
        id, idMember, memberName, memberPhone, memberEmail, memberPhotoUrl,
        idTenant, user, 
        idConsultant, consultantName, consultantEmail, consultantPhone, consultantPhotoUrl,
        addOrUpdateMember
    ]);

    return {
        isProcessing,
        saleSuccessData,
        showReceiptModal,
        handleCloseReceipt,
        handleFinalizeSale
    };
};
